/**
 * Bring a screen's named section into view and put the keyboard on it — what makes a sub-nav item
 * real. **Z-7.**
 *
 * ⚠️ **All 25 sub-nav items navigated to the screen they were already on and did nothing.** That is
 * the same failure as a disabled control with no explanation: the affordance says "there is
 * somewhere to go" and there is not. Each item now names a section id, and this is what goes there.
 *
 * Extracted from `App.tsx` rather than left inline **because it could not be verified in a browser
 * pane**: focus does not register in a background tab and `scrollIntoView({behavior:'smooth'})` is
 * a silent no-op there, so both halves of this function read as broken when they are not. The
 * retry, the motion fallback and the focus re-check are all subtle enough to deserve tests that
 * cannot lie about them.
 */

export interface RevealOptions {
  /** Injected so a test drives the clock instead of waiting on one. */
  schedule?: (fn: () => void, ms: number) => void;
  /** Injected for the same reason — the first attempt is deferred a tick in the browser. */
  defer?: (fn: () => void) => void;
  /**
   * ⚠️ Accepted and unused, on purpose: `scrollIntoView` here is always `auto`, so there is no
   * motion to suppress. Kept in the signature so the reasoning is discoverable from the call site
   * rather than only from the body — the first version DID animate, and it broke the feature.
   */
  prefersReducedMotion?: () => boolean;
  /** Bounded: a section that never appears is a nav-table defect, not something to spin on. */
  maxAttempts?: number;
}

export function revealSection(sectionId: string, options: RevealOptions = {}): void {
  const schedule = options.schedule ?? ((fn, ms) => void window.setTimeout(fn, ms));
  /* ⚠️ **`setTimeout`, not `requestAnimationFrame`, and this was a real bug.** rAF is suspended in
     a background or hidden tab, so the first attempt never ran there and the sub-nav item silently
     did nothing — measured: the handler logged, and nothing moved. A frame boundary buys nothing
     here (this is one scroll, not an animation) and costs the whole feature whenever the tab is not
     in front. */
  const defer = options.defer ?? ((fn) => void window.setTimeout(fn, 0));
  const maxAttempts = options.maxAttempts ?? 12;

  let attempts = 0;

  const tryReveal = () => {
    const el = document.getElementById(sectionId);
    if (el) {
      /* ⚠️ **`auto`, deliberately — arrival must not depend on an animation.**
         The first version used `behavior: 'smooth'` on the first attempt and fell back to `auto`
         on later ones. That looked careful and was broken: smooth **silently does nothing** in
         some environments (measured — a background tab scrolled 0px where `auto` moved 828), and
         because the focus below then succeeded, the function returned having scrolled nowhere. The
         nav item did nothing, which is the exact defect Z-7 exists to fix, reintroduced by the
         polish. A jump is also the honest interaction for a navigation control, and it satisfies
         reduced-motion by construction rather than by a check that can be forgotten. */
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      /* `preventScroll` so focus does not scroll a second time and land somewhere slightly
         different from where `scrollIntoView` just put the section. */
      el.focus({ preventScroll: true });
      /* ⚠️ **Stops once focus has actually TAKEN, not once it has been requested.** A destination
         screen paints a skeleton and then re-renders with its content; focusing during that window
         puts focus on a node React is about to replace, and the browser drops it to `<body>`. */
      if (document.activeElement === el) return;
    }
    attempts += 1;
    if (attempts <= maxAttempts) schedule(tryReveal, 150);
  };

  /* Deferred a tick: the destination screen may not have mounted yet when the route changes. */
  defer(tryReveal);
}
